import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { routeByPath, primaryNav, GROUPS, groupHubs, servicesByGroup } from "@/data/routes";

const GROUP_KEYS = ["websites", "ads", "search", "ai"];

const ANCHORS = {
  "/services/wordpress": [["Oxygen Builder", "#oxygen"], ["Beaver Builder", "#beaver-builder"], ["Gutenberg", "#gutenberg"]],
  "/services/website-builders": [["Webflow", "#webflow"], ["Wix", "#wix"], ["Duda", "#duda"], ["Framer", "#framer"]],
  "/services/ai-agents": [["Knowledge assistants & chatbots", "#assistants"]],
};

const ServicesMenu = ({ open, onClose }) => (
  <div className={`bliss-megamenu ${open ? "is-open" : ""}`} role="menu" aria-label="Services" data-testid="services-megamenu" hidden={!open}>
    <div className="bliss-container bliss-megamenu-grid">
      {GROUP_KEYS.map((g) => (
        <div key={g} className="bliss-megamenu-col">
          <Link to={groupHubs[g]} className="bliss-megamenu-heading" onClick={onClose} data-testid={`menu-group-${g}`}>
            {GROUPS[g]}
          </Link>
          <ul>
            {servicesByGroup(g).map((r) => (
              <li key={r.path}>
                <Link to={r.path} onClick={onClose} data-testid={`menu-item-${r.path.split("/").pop()}`}>
                  {r.nav || r.h1}
                </Link>
                {ANCHORS[r.path] && (
                  <ul className="bliss-megamenu-anchors">
                    {ANCHORS[r.path].map(([label, hash]) => (
                      <li key={hash}>
                        <Link to={`${r.path}${hash}`} onClick={onClose} data-testid={`menu-anchor-${hash.slice(1)}`}>
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

export const Header = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const wrapRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setServicesOpen(false);
        setMobileOpen(false);
      }
    };
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setServicesOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header className="bliss-header" data-testid="site-header" ref={wrapRef}>
      <div className="bliss-container bliss-header-inner">
        <Logo />
        <nav className="bliss-nav" aria-label="Primary" data-testid="primary-nav">
          <button
            type="button"
            className="bliss-nav-btn"
            aria-expanded={servicesOpen}
            aria-haspopup="true"
            onClick={() => setServicesOpen((v) => !v)}
            data-testid="nav-services-toggle"
          >
            Services <ChevronDown size={14} className={servicesOpen ? "rot" : ""} aria-hidden="true" />
          </button>
          {primaryNav.slice(1).map((p) => (
            <Link key={p} to={p} data-testid={`nav-link-${p.slice(1)}`}>
              {routeByPath[p].nav}
            </Link>
          ))}
        </nav>
        <Link to="/marketing-fit" className="bliss-cta bliss-cta-header" data-testid="header-fit-cta">
          Find My Marketing Fit <ArrowRight size={16} aria-hidden="true" />
        </Link>
        <button
          type="button"
          className="bliss-mobile-toggle"
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <ServicesMenu open={servicesOpen} onClose={() => setServicesOpen(false)} />

      {mobileOpen && (
        <div className="bliss-mobile-menu" data-testid="mobile-menu">
          <nav aria-label="Mobile">
            <details data-testid="mobile-services-group">
              <summary>Services</summary>
              {GROUP_KEYS.map((g) => (
                <details key={g} className="bliss-mobile-subgroup">
                  <summary>{GROUPS[g]}</summary>
                  <Link to={groupHubs[g]} className="bliss-mobile-hub">
                    All {GROUPS[g]}
                  </Link>
                  {servicesByGroup(g).map((r) => (
                    <Link key={r.path} to={r.path}>
                      {r.nav || r.h1}
                    </Link>
                  ))}
                </details>
              ))}
            </details>
            {primaryNav.slice(1).map((p) => (
              <Link key={p} to={p} className="bliss-mobile-link">
                {routeByPath[p].nav}
              </Link>
            ))}
            <Link to="/marketing-fit" className="bliss-cta" data-testid="mobile-fit-cta">
              Find My Marketing Fit <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
